import type { TFunction } from "i18next";
import { useMemo } from "react";

import type { ChartConfig } from "@/components/ui/v2/chart";
import type { ActiveCampaignDistributionItem } from "@/features/home/api/useActiveCampaignDistribution";
import { translateIntegrationDisplayName } from "@/features/integrations/utils/translate-integration-display-name";

// Categorical palette: 8 distinct saturated hues, index-assigned per integration.
// Tokens live in index.css so dark/light mode can adapt.
export const PALETTE = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "var(--chart-6)",
  "var(--chart-7)",
  "var(--chart-8)",
] as const;
export const OTHER_FILL = "var(--color-neutral)";

export const TOP_N = 8;
export const OTHER_KEY = "__other__";

export interface Slice {
  key: string;
  label: string;
  value: number;
  fill: string;
  isOther: boolean;
}

export function labelFor(
  item: ActiveCampaignDistributionItem,
  t: TFunction,
  i18n: { exists: (k: string) => boolean },
): string {
  return (
    translateIntegrationDisplayName(item.name, t, i18n) ||
    item.displayName ||
    item.name
  );
}

interface UseDistributionSlicesParams {
  distribution: ActiveCampaignDistributionItem[];
  selected: string[] | null;
  t: TFunction;
  i18n: { exists: (k: string) => boolean; language: string };
}

export function useDistributionSlices({
  distribution,
  selected,
  t,
  i18n,
}: UseDistributionSlicesParams) {
  return useMemo(() => {
    const visible = distribution
      .filter((d) => selected === null || selected.includes(d.name))
      .sort((a, b) => b.campaignCount - a.campaignCount);

    const head = visible.slice(0, TOP_N);
    const tail = visible.slice(TOP_N);
    const tailCampaignSum = tail.reduce((s, d) => s + d.campaignCount, 0);

    const config: ChartConfig = {};
    const datapoints: Slice[] = head.map((item, i) => {
      const fill = PALETTE[i] ?? PALETTE[PALETTE.length - 1];
      const label = labelFor(item, t, i18n);
      config[item.name] = { label, color: fill };
      return {
        key: item.name,
        label,
        value: item.campaignCount,
        fill,
        isOther: false,
      };
    });

    if (tail.length > 0) {
      const label = t("home.activeCampaignDistribution.other");
      config[OTHER_KEY] = { label, color: OTHER_FILL };
      datapoints.push({
        key: OTHER_KEY,
        label,
        value: tailCampaignSum,
        fill: OTHER_FILL,
        isOther: true,
      });
    }

    return {
      rows: datapoints,
      chartConfig: config,
      hasData: datapoints.length > 0,
    };
  }, [distribution, selected, t, i18n]);
}
